/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useLazyQuery, useMutation } from '@apollo/client' // Npm: Apollo client.
import { HiMinusCircle, HiPlusCircle } from 'react-icons/hi' // Npm: React icons.
import {
  Button,
  Flex,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import MccMncSelector from 'components/MccMncSelector'
import VendorAccountSelector from 'components/VendorAccountSelector'
import { MemoizedInput } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import CBRRoutePlanReadQuery from './__query__/index.cbrroutePlan.read.query'
import RoutePlanUpdateMutation from './__mutation__/index.cbrroutePlan.update.mutation'
import CBRRoutePlanCreateMutation from './__mutation__/index.cbrroutePlan.create.mutation'
import VendorAccountAttachCBRRoutePlanMutation from './__mutation__/index.vendorAccount.attachcbrRoutePlan.mutation'


/*
 * OBJECTS
 */
const Index = ({
  isOpen,
  isCreateOnly,
  onClose,
  passOn,
  refetch
}) => {
  // Hook assignment.
  const [vendorAccount, setVendorAccount] = React.useState(void 0)
  const [mappingForms, setMappingForms] = React.useState([1])
  const [error, setError] = React.useState('')
  const [formVersion, _ResetFormInputs] = React.useReducer(j => j + 1, 0)
  const [selectedVendors, setSelectedVendors] = React.useState({})
  const [QueryCBRRoutePlanRead, QueryCBRRoutePlanReadResponse] = useLazyQuery(CBRRoutePlanReadQuery)
  const [CBRMutationRoutePlanCreate, CBRMutationRoutePlanCreateResponse] = useMutation(CBRRoutePlanCreateMutation)
  const [CBRMutationRoutePlanUpdate, CBRMutationRoutePlanUpdateResponse] = useMutation(RoutePlanUpdateMutation)
  const [MutationVendorAccountAttachCBRRoutePlan, MutationVendorAccountAttachCBRRoutePlanResponse] = useMutation(VendorAccountAttachCBRRoutePlanMutation)
  const _formDataRef = React.useRef({})
  const _isCurrentViewMobile = useBreakpointValue({ 'base': false, 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Object assignment.
  const _AttachVendorAccountToCBRRoutePlan = async __id => {
    // Only Execute if route has data.
    if (!_.isEmpty(vendorAccount) && vendorAccount.includes('(') && vendorAccount.includes(')')) {
      // Const assignment.
      const vendorAccountId = vendorAccount.split(' ')[1].replace('(', '').replace(')', '')
      // Execute mutation.
      const _MutationVendorAccountAttachCBRRoutePlan = await MutationVendorAccountAttachCBRRoutePlan({
        'variables': {
          'routePlanId': __id,
          'vendorAccountId': vendorAccountId
        }
      })

      // If mutation caught an exception then report failure.
      if (_MutationVendorAccountAttachCBRRoutePlan instanceof Error) return _MutationVendorAccountAttachCBRRoutePlan

      // Style Guide.
      toast(_.every(_.pluck(_MutationVendorAccountAttachCBRRoutePlan?.data?.VendorAccountAttachCBRRoutePlan, 'status'), i => 'UPDATE_SUCCESSFUL' === i) ? 'Successfully attached the vendor with CBR routePlan' : 'Something went wrong. Please try again after some time.')

      // On successful bind close modal.
      // if (_.every(_.pluck(_MutationVendorAccountAttachCBRRoutePlan?.data?.VendorAccountAttachCBRRoutePlan, 'status'), i => 'UPDATE_SUCCESSFUL' === i)) onClose()
    }

    // Report void 0.
    return void 0
  }
  const _SubmitForm = async e => {
    // Prevent default behavior.
    e.preventDefault()

    // Reset error.
    setError('')

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      'mcc': JoiBrowser.string().required(),
      'mnc': JoiBrowser.array().items(JoiBrowser.string().required()).required(),
      'mappings': JoiBrowser.array().items(JoiBrowser.object({
        'vendorAccount': JoiBrowser.string().required(),
        'regex': JoiBrowser.array().items(JoiBrowser.string().required()).min(1).required()
      })).min(1).required()
    }).options({ 'allowUnknown': true })

    // Make sure that mnc is array.
    if (!isCreateOnly && !_.isArray(_formDataRef.current?.mnc)) _formDataRef.current = { ..._formDataRef.current, 'mnc': [(_formDataRef.current?.mnc)?.split('(')[1]?.split(')')[0]] }

    const _mappings = _.compact(mappingForms.map((__form, __index) => {
      const _vendorAccount = _formDataRef.current?.[`vendorAccount__${__index}`]
      const _regex = _formDataRef.current?.[`regex__${__index}`]

      if (_.isEmpty(_vendorAccount) && _.isEmpty(_regex)) return void 0
      return { 'vendorAccount': _vendorAccount, 'regex': _regex }
    }))

    if (_.isEmpty(_mappings)) return setError('vendorAccount is required.')
    if (_.some(_mappings, j => _.isEmpty(j?.vendorAccount))) return setError('vendorAccount is required.')
    if (_.some(_mappings, j => _.isEmpty(j?.regex))) return setError('regex is required.')

    _formDataRef.current = { ..._.pick(_formDataRef.current, _.identity), 'mappings': _mappings }

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    /*
     * Update _formDataRef with routePlanId
     * if it doesn't exist and also make sure that mode
     * is not create only.
     */
    if (!isCreateOnly && _.isEmpty(_formDataRef.current?.routePlanId)) _formDataRef.current = { ..._formDataRef.current, 'routePlanId': passOn?.routePlanId }
    if (isCreateOnly && _.isEmpty(_formDataRef.current?.routeId)) _formDataRef.current = { ..._formDataRef.current, 'routeId': passOn?.routeId }

    /*
     * Update _formDataRef with input and routePlan
     * if it doesn't exist. Also make sure that mode is
     * not create only.
     */
    _formDataRef.current = ({ ..._formDataRef.current, 'input': [{ ..._.omit(_formDataRef.current, 'input') }] })

    // Execute update mutation.
    const _CBRMutationRoutePlanUpdate = await [isCreateOnly ? CBRMutationRoutePlanCreate : CBRMutationRoutePlanUpdate]?.[0]({
      'variables': {
        'routePlanId': _.isEmpty(passOn?.routePlanIds) ? isCreateOnly ? '' : passOn?.routePlanId : void 0,
        'routePlanIds': _.isEmpty(passOn?.routePlanIds) ? void 0 : isCreateOnly ? '' : passOn?.routePlanIds,

        'input': _.pick(_formDataRef.current, 'input').input.map(j => {
          let _mcc, _mnc

          // Create case: send mcc, mnc, and mappings array with vendorAccountId and regex.
          if (isCreateOnly) {
            let _mappings = _.compact((j?.mappings ?? []).map(r => {
              let _vendorAccount = r?.vendorAccount
              if (r?.vendorAccount && (r?.vendorAccount?.includes('(') || r?.vendorAccount?.includes(')')))
                _vendorAccount = r?.vendorAccount.split('(')[1].split(')')[0]
              return ({ 'vendorAccountId': _vendorAccount, 'regex': r?.regex })
            }))
            _mcc = j?.mcc
            _mnc = j?.mnc
            if (j?.mcc && (j?.mcc?.includes('(') || j?.mcc?.includes(')')))
              _mcc = j.mcc.split('(')[1].split(')')[0]
            if (j?.mnc && 0 < j?.mnc?.length)
              _mnc = _.compact(_.flatten(j?.mnc.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))
            return ({ 'mcc': _mcc, 'mnc': _mnc, 'mappings': _mappings, 'routeId': passOn?.routeId })
          }

          // Update case: directly send vendorAccountId and regex array.
          let _vendorAccount = j?.mappings?.[0]?.vendorAccount
          if (_vendorAccount && (_vendorAccount.includes('(') || _vendorAccount.includes(')')))
            _vendorAccount = _vendorAccount.split('(')[1].split(')')[0]
          _mcc = j?.mcc
          _mnc = j?.mnc
          if (j?.mcc && (j?.mcc?.includes('(') || j?.mcc?.includes(')')))
            _mcc = j.mcc.split('(')[1].split(')')[0]
          if (j?.mnc && 0 < j?.mnc?.length)
            _mnc = _.compact(_.flatten(j?.mnc.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))

          return ({
            'vendorAccountId': _vendorAccount,
            'mcc': _mcc,
            'mnc': _mnc,
            'regex': j?.mappings?.[0]?.regex
          })
        })
      }
    })

    // If mutation caught an exception then report failure.
    if (_CBRMutationRoutePlanUpdate instanceof Error) return _CBRMutationRoutePlanUpdate

    // Local variable.
    let _routePlanIds

    // If Update contains data then update routePlanIds.
    if (!_.isEmpty(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanUpdate)) _routePlanIds = _.compact(_.pluck(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanUpdate, 'id'))
    if (!_.isEmpty(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanCreate)) _routePlanIds = _.compact(_.pluck(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanCreate, 'id'))

    // Check if create was successful.
    if (!_.isEmpty(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanCreate) && _.every(_.pluck(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanCreate, 'status') ?? [], j => 'CREATE_SUCCESSFUL' === j)) {
      // Style guide.
      toast('Successfully upserted the route plan.')

      // On successful create close modal.
      onClose?.()
      await refetch?.()
      return

    }
    // Check if update was successful.
    if (!_.isEmpty(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanUpdate) && _.every(_.pluck(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanUpdate, 'status') ?? [], j => 'UPDATE_SUCCESSFUL' === j)) {
      // Attach vendor account and route to customer.
      !_.isEmpty(_routePlanIds) && _.isArray(_routePlanIds) && await _AttachVendorAccountToCBRRoutePlan(_routePlanIds)
      // Style guide.
      toast('Successfully upserted the route plan.')

      // Report failure.
      onClose?.()
      await refetch?.()
      return

    }

    // Style Guide.
    toast((_.isEmpty(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanCreate) ? [0 === _.pluck(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanUpdate, 'message')?.length ? 'Same plan already exists. kindly delete it or modify it instead.' : _.pluck(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanUpdate, 'message')]?.join(' ') : _.pluck(_CBRMutationRoutePlanUpdate?.data?.CBRRoutePlanCreate, 'message')?.join(' ')))

    // Report void.
    return void 0
  }

  // Event handler.
  React.useEffect(() => {
    // Do not call read query while modal is closed.
    if (false === isOpen) return void 0

    // Reset create form without hitting route plan read query.
    if (isCreateOnly) {
      _formDataRef.current = {}
      setMappingForms([1])
      setSelectedVendors({})
      setVendorAccount(void 0)
      _ResetFormInputs()
      return void 0
    }

    // Do not call read query without route plan id.
    if (_.isEmpty(passOn?.routePlanId)) return void 0

    // Local variable.
    let _isActive = true

    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QueryCBRRoutePlanReadQuery = await QueryCBRRoutePlanRead({ 'variables': { 'routePlanId': passOn?.routePlanId } })

      // If query caught an exception then report failure.
      if (_QueryCBRRoutePlanReadQuery instanceof Error) return _QueryCBRRoutePlanReadQuery

      // Avoid updating state after component closes/unmounts.
      if (!_isActive) return void 0

      /*
       * If routePlan details fetch complete then
       * update its value.
       */
      if (_QueryCBRRoutePlanReadQuery?.data?.CBRRoutePlanRead) {
        // Const assignment.
        const _routePlanRead = _.first(_QueryCBRRoutePlanReadQuery?.data?.CBRRoutePlanRead)

        // Update form data.
        _formDataRef.current = {
          'routePlanId': passOn?.routePlanId,
          'createdAt': _routePlanRead?.createdAt,
          'updatedAt': _routePlanRead?.updatedAt,
          'mcc': _routePlanRead?.Mcc?.mcc ? `${_routePlanRead?.Mcc?.countryName} (${_routePlanRead?.Mcc?.mcc})` : void 0,
          'mnc': _routePlanRead?.Mnc?.mnc ? `(${_routePlanRead?.Mnc?.mnc}) ${_routePlanRead?.Mnc?.network}` : void 0,
          'countryName': _routePlanRead?.Mcc?.countryName,
          'regex__0': _routePlanRead?.regex,
          'vendorAccount__0': 0 < _QueryCBRRoutePlanReadQuery?.data?.CBRRoutePlanRead?.length ? _routePlanRead?.VendorAccount?.displayName ? `${_routePlanRead?.VendorAccount?.displayName} (${_routePlanRead?.VendorAccount?.id})` : void 0 : void 0
        }
        setMappingForms([1])
        setSelectedVendors({})

        // Reset form inputs after route plan read.
        return _ResetFormInputs()
      }

      // Report failure.
      return void 0
    }; _Async()

    // Cleanup active flag.
    return () => { _isActive = false }
  }, [QueryCBRRoutePlanRead, _ResetFormInputs, isCreateOnly, isOpen, passOn?.routePlanId])
  // Const assignment.
  const _isLoading = MutationVendorAccountAttachCBRRoutePlanResponse.loading || CBRMutationRoutePlanUpdateResponse.loading || CBRMutationRoutePlanCreateResponse.loading
  const _isInputDisabled = isCreateOnly ? false : _isLoading || QueryCBRRoutePlanReadResponse.loading

  // Return component.
  return (
    <form onSubmit={_SubmitForm} className='routePlanUpsert' key={formVersion}>
      <Flex gap='22px' flexDir='column' wrap='nowrap' w='100%' overflow='visible'>

        <Flex width='100%' gap='18px' direction='column' overflow='visible'>
          {mappingForms.map((__form, __index) => (
            <Flex key={`mapping__${__index}`} width='100%' gap='18px' direction={{ 'base': 'column', 'md': 'row' }} alignItems={{ 'base': 'stretch', 'md': 'flex-end' }} position='relative'
              zIndex={mappingForms.length - __index}  >
              <VendorAccountSelector
                disabled={_isInputDisabled}
                name={`vendorAccount__${__index}`}
                label='Vendor Account'
                placeholder='e.g. "Awesome Vendor Account"'
                data={selectedVendors[`vendorAccount__${__index}`] ?? _formDataRef?.current?.[`vendorAccount__${__index}`]}
                isRequired={true}
                error={error}
                height='40px'
                isInvalid={error?.includes('vendorAccount')}
                onChange={({ target }) => {
                  const { name, value } = target
                  _formDataRef.current = {
                    ..._formDataRef?.current,
                    [name]: value
                  }
                  setSelectedVendors(prev => ({ ...prev, [name]: value }))
                  setVendorAccount(value)
                }}

              />
              <MemoizedInput
                disabled={_isInputDisabled}
                name={`regex__${__index}`}
                label='Regex Pattern (Enter/comma/ Tab )'
                placeholder='e.g. "enter the value"'
                value={_formDataRef?.current?.[`regex__${__index}`]}
                error={error}
                isMultiple={true}
                separators={[',', 'Enter', 'Tab', ' ']}
                isInvalid={error?.includes('regex')}
                onChange={({ target }) => {
                  // Over spreading.
                  const { name, value } = target

                  // Update form data.
                  _formDataRef.current = {
                    ..._formDataRef.current,
                    [name]: value
                  }
                }}
              />
              <Button
                disabled={_isInputDisabled}
                variant='basic'
                type='button'
                h='40px'
                minH='40px'
                bg={__index === (mappingForms.length - 1) ? 'brand.400' : 'red.400'}
                onClick={() => {
                  if (__index !== (mappingForms.length - 1)) {
                    const temp = _.pick(_formDataRef.current, (__, key) => !key.startsWith('vendorAccount__') && !key.startsWith('regex__'))
                    let newIndex = 0

                    mappingForms.forEach((__formItem, i) => {
                      if (i !== __index) {
                        temp[`vendorAccount__${newIndex}`] = _formDataRef.current[`vendorAccount__${i}`]
                        temp[`regex__${newIndex}`] = _formDataRef.current[`regex__${i}`]
                        newIndex++
                      }
                    })

                    _formDataRef.current = temp
                    setMappingForms(mappingForms.filter((u, i) => i !== __index))
                    _ResetFormInputs()
                    return
                  }

                  setMappingForms([...mappingForms, 1])
                }}
                _hover={{
                  bg: __index === (mappingForms.length - 1) ? 'brand.500' : 'red.500'
                }}
                _active={{
                  bg: __index === (mappingForms.length - 1) ? 'brand.600' : 'red.600'
                }}
                minW='50px'>
                {__index === (mappingForms.length - 1)
                  ? <HiPlusCircle size={16} />
                  : <HiMinusCircle size={16} />
                }
              </Button>
            </Flex>
          ))}
        </Flex>

        {/* MCC + MNC */}
        <Flex width='100%' gap='22px' direction={{ 'base': 'column', 'md': 'row' }} >
          <MccMncSelector
            isRequired={true}
            disabled={_isInputDisabled}
            mccValue={_formDataRef?.current?.mcc}
            mncValue={_formDataRef?.current?.mnc}
            inValidMnc={error?.includes('mnc')}
            inValidMcc={error?.includes('mcc')}
            onChange={i => {
              // Update form data.
              _formDataRef.current = ({ ..._formDataRef.current, 'mnc': i.mnc, 'mcc': i.mcc })

            }}
          />
        </Flex>


      </Flex>

      {/* Submit Button */}
      <Flex
        mt='22px'
        alignItems={_isCurrentViewMobile ? 'flex-end' : 'center'}
        justifyContent='flex-end'
        flexDir='row'>
        <SubmitButton
          mt={0}
          disabled={_isInputDisabled}
          onSubmit={_SubmitForm}
          defaultText={isCreateOnly ? 'Create CBR RoutePlan' : 'Update CBR RoutePlan'}
          isLoading={_isLoading} />
      </Flex>
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isCreateOnly': PropTypes.bool,
  'isOpen': PropTypes.bool,
  'onClose': PropTypes.func,
  'refetch': PropTypes.func,
  'passOn': PropTypes.object,
  'submitRef': PropTypes.object,
  'disabled': PropTypes.bool,
  'isUpsertMixerContext': PropTypes.bool,
  'setSubmitIsLoading': PropTypes.func
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(React.memo(Index))
